import { useEffect, useMemo, useState } from "react";
import { useCustom, useCustomMutation, usePermissions, useTranslate } from "@refinedev/core";
import {
  PlusCircleSolidIcon,
  Button,
  CheckboxField,
  Field,
  Modal,
  SelectField,
  StatusBadge,
  type Column,
  type StatusTone,
} from "@fawkes/ui";

import type { SettingsContext } from "./tiles/types";
import "./BreedWeightRanges.css";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

// Gewichtsbandbreedtes beheerscherm (Epic 15, stories 15-2/15-3) — the React port of the Jmix
// `RaWeightRangeFragment`. A diersoort + ras picker scopes the grid of weight-range rules for one breed
// (concepts included); the toolbar mirrors the fragment buttons (toevoegen / wijzigen / verwijderen /
// activeer+accordeer / AI-voorstel). New rules are scoped to the active settings context (Organisatie =
// platform, a vestiging = organisation-specific). Backed by the hexagonal `/api/breed-weight-ranges`
// resource, all gated by ROLE_BREED_WEIGHT_ADMIN.

const ADMIN_AUTHORITY = "ROLE_BREED_WEIGHT_ADMIN";

interface Option {
  id: number;
  naam: string;
}

interface Rule {
  id: number;
  vestigingId: number | null;
  scope: string;
  leeftijdVanMaanden: number | null;
  leeftijdTotMaanden: number | null;
  geslacht: string | null;
  gecastreerd: boolean | null;
  minKg: number | null;
  ideaalKg: number | null;
  maxKg: number | null;
  sourceType: string | null;
  sourceName: string | null;
  sourceUrl: string | null;
  confidenceLevel: string | null;
  aiModel: string | null;
  reviewed: boolean | null;
  actief: boolean | null;
  status: string;
  changeReason: string | null;
}

interface EditState {
  id: number | null;
  ageFrom: string;
  ageTo: string;
  sex: string; // "" | "M" | "V"
  neutered: string; // "" | "Wel" | "Niet"
  min: string;
  ideal: string;
  max: string;
  confidence: string;
  sourceName: string;
  sourceUrl: string;
  actief: boolean;
  reviewed: boolean;
  changeReason: string;
}

function emptyEdit(): EditState {
  return {
    id: null,
    ageFrom: "0",
    ageTo: "1200",
    sex: "",
    neutered: "",
    min: "",
    ideal: "",
    max: "",
    confidence: "HIGH",
    sourceName: "",
    sourceUrl: "",
    actief: false,
    reviewed: false,
    changeReason: "",
  };
}

function parseWeight(raw: string): number | null {
  if (!raw || raw.trim() === "") return null;
  const n = Number(raw.trim().replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function fmtWeight(v: number | null): string {
  if (v == null) return "—";
  return String(v);
}

function statusTone(status: string): StatusTone {
  if (status === "ACTIEF") return "success";
  if (status === "CONCEPT") return "warning";
  return "neutral";
}

export function BreedWeightRanges({
  context,
  onBack,
}: {
  context: SettingsContext;
  onBack: () => void;
}) {
  const t = useTranslate();
  const { data: permissions } = usePermissions<string[]>();
  const canEdit = Array.isArray(permissions) && permissions.includes(ADMIN_AUTHORITY);

  const { mutate: mutateCustom, isLoading: isSaving } = useCustomMutation();

  const [selectedSpecies, setSelectedSpecies] = useState("");
  const [selectedBreed, setSelectedBreed] = useState("");
  const [version, setVersion] = useState(0);
  const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = () => setVersion((v) => v + 1);

  const { data: speciesData } = useCustom<Option[]>({
    url: "breed-weight-ranges/species",
    method: "get",
    queryOptions: { enabled: canEdit },
  });
  const species = useMemo(() => speciesData?.data ?? [], [speciesData]);

  // Default the species selector to "hond" (dog) when present — mirrors the Dieren hub.
  useEffect(() => {
    if (selectedSpecies !== "" || species.length === 0) return;
    const hond = species.find((s) => s.naam.toLowerCase().includes("hond"));
    setSelectedSpecies(String((hond ?? species[0]).id));
  }, [species, selectedSpecies]);

  const { data: breedData } = useCustom<Option[]>({
    url: `breed-weight-ranges/breeds?species=${selectedSpecies}`,
    method: "get",
    queryOptions: {
      enabled: canEdit && selectedSpecies !== "",
      queryKey: ["breed-weight-breeds", selectedSpecies],
    },
  });
  const breeds = useMemo(() => breedData?.data ?? [], [breedData]);

  // Reset the breed selection whenever the species changes.
  useEffect(() => {
    setSelectedBreed("");
    setSelectedRuleId(null);
  }, [selectedSpecies]);

  const { data: rulesData, isLoading: rulesLoading } = useCustom<Rule[]>({
    url: `breed-weight-ranges?rasId=${selectedBreed}`,
    method: "get",
    queryOptions: {
      enabled: canEdit && selectedBreed !== "",
      queryKey: ["breed-weight-rules", selectedBreed, version],
    },
  });
  const rules = useMemo(() => rulesData?.data ?? [], [rulesData]);
  const selectedRule = rules.find((r) => r.id === selectedRuleId) ?? null;

  if (!canEdit) {
    return (
      <div className="bwr-section">
        <div className="bwr-head">
          <h2 className="bwr-title">{t("instellingen.gewichtsbandbreedte.title")}</h2>
          <Button variant="secondary" onClick={onBack}>
            {t("common.back")}
          </Button>
        </div>
        <p className="bwr-empty">{t("instellingen.gewichtsbandbreedte.noAccess")}</p>
      </div>
    );
  }

  function startCreate() {
    setFormError(null);
    setEdit(emptyEdit());
  }

  function startEdit() {
    if (!selectedRule) return;
    setFormError(null);
    setEdit({
      id: selectedRule.id,
      ageFrom: String(selectedRule.leeftijdVanMaanden ?? 0),
      ageTo: String(selectedRule.leeftijdTotMaanden ?? 1200),
      sex: selectedRule.geslacht ?? "",
      neutered: selectedRule.gecastreerd == null ? "" : selectedRule.gecastreerd ? "Wel" : "Niet",
      min: selectedRule.minKg == null ? "" : String(selectedRule.minKg),
      ideal: selectedRule.ideaalKg == null ? "" : String(selectedRule.ideaalKg),
      max: selectedRule.maxKg == null ? "" : String(selectedRule.maxKg),
      confidence: selectedRule.confidenceLevel ?? "HIGH",
      sourceName: selectedRule.sourceName ?? "",
      sourceUrl: selectedRule.sourceUrl ?? "",
      actief: Boolean(selectedRule.actief),
      reviewed: Boolean(selectedRule.reviewed),
      changeReason: selectedRule.changeReason ?? "",
    });
  }

  function validate(e: EditState): string | null {
    const from = Number(e.ageFrom);
    const to = Number(e.ageTo);
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || to < from) {
      return t("instellingen.gewichtsbandbreedte.err.age");
    }
    const min = parseWeight(e.min);
    const max = parseWeight(e.max);
    const ideal = e.ideal.trim() === "" ? null : parseWeight(e.ideal);
    if (min == null || max == null) {
      return t("instellingen.gewichtsbandbreedte.err.minmaxRequired");
    }
    if (min <= 0 || max <= 0) {
      return t("instellingen.gewichtsbandbreedte.err.positive");
    }
    if (min >= max) {
      return t("instellingen.gewichtsbandbreedte.err.minLtMax");
    }
    if (ideal != null && (ideal < min || ideal > max)) {
      return t("instellingen.gewichtsbandbreedte.err.idealBetween");
    }
    return null;
  }

  function save() {
    if (!edit) return;
    const err = validate(edit);
    if (err) {
      setFormError(err);
      return;
    }
    const values: Record<string, unknown> = {
      leeftijdVanMaanden: Number(edit.ageFrom),
      leeftijdTotMaanden: Number(edit.ageTo),
      geslacht: edit.sex === "" ? null : edit.sex,
      gecastreerd: edit.neutered === "" ? null : edit.neutered === "Wel",
      minKg: parseWeight(edit.min),
      ideaalKg: edit.ideal.trim() === "" ? null : parseWeight(edit.ideal),
      maxKg: parseWeight(edit.max),
      confidenceLevel: edit.confidence,
      sourceName: edit.sourceName.trim() === "" ? null : edit.sourceName.trim(),
      sourceUrl: edit.sourceUrl.trim() === "" ? null : edit.sourceUrl.trim(),
      actief: edit.actief,
      reviewed: edit.reviewed,
      changeReason: edit.changeReason.trim() === "" ? null : edit.changeReason.trim(),
    };
    if (edit.id == null) {
      values.rasId = Number(selectedBreed);
      values.vestigingId = context.vestigingId;
    }
    mutateCustom(
      {
        url: edit.id == null ? "breed-weight-ranges" : `breed-weight-ranges/${edit.id}`,
        method: edit.id == null ? "post" : "put",
        values,
      },
      {
        onSuccess: () => {
          setEdit(null);
          setNotice(t("instellingen.gewichtsbandbreedte.saved"));
          refresh();
        },
      },
    );
  }

  function deactivate() {
    if (!selectedRule) return;
    mutateCustom(
      { url: `breed-weight-ranges/${selectedRule.id}`, method: "delete", values: {} },
      {
        onSuccess: () => {
          setNotice(t("instellingen.gewichtsbandbreedte.deactivated"));
          refresh();
        },
      },
    );
  }

  function review() {
    if (!selectedRule) return;
    mutateCustom(
      { url: `breed-weight-ranges/${selectedRule.id}/review`, method: "post", values: {} },
      {
        onSuccess: () => {
          setNotice(t("instellingen.gewichtsbandbreedte.activated"));
          refresh();
        },
      },
    );
  }

  function aiSuggest() {
    if (selectedBreed === "") return;
    setNotice(t("instellingen.gewichtsbandbreedte.aiBusy"));
    mutateCustom(
      {
        url: "breed-weight-ranges/ai-suggest",
        method: "post",
        values: { rasId: Number(selectedBreed), vestigingId: context.vestigingId },
      },
      {
        onSuccess: (data) => {
          const created = Array.isArray(data?.data) ? data.data.length : 0;
          setNotice(
            created > 0
              ? t("instellingen.gewichtsbandbreedte.aiAdded", { args: [created] })
              : t("instellingen.gewichtsbandbreedte.aiNone"),
          );
          refresh();
        },
      },
    );
  }

  const speciesOptions = species.map((s) => ({ value: String(s.id), label: s.naam }));
  const breedOptions = [
    { value: "", label: t("instellingen.gewichtsbandbreedte.choosBreed") },
    ...breeds.map((b) => ({ value: String(b.id), label: b.naam })),
  ];

  const columns: Column<Rule>[] = [
    {
      header: t("instellingen.gewichtsbandbreedte.col.age"),
      cell: (r) => `${r.leeftijdVanMaanden ?? 0}–${r.leeftijdTotMaanden ?? 1200} ${t("instellingen.gewichtsbandbreedte.months")}`,
    },
    { header: t("instellingen.gewichtsbandbreedte.col.sex"), cell: (r) => r.geslacht ?? "—" },
    {
      header: t("instellingen.gewichtsbandbreedte.col.neutered"),
      cell: (r) => (r.gecastreerd == null ? "—" : r.gecastreerd ? t("common.yes") : t("common.no")),
    },
    { header: t("instellingen.gewichtsbandbreedte.col.min"), cell: (r) => fmtWeight(r.minKg), align: "right" },
    { header: t("instellingen.gewichtsbandbreedte.col.ideal"), cell: (r) => fmtWeight(r.ideaalKg), align: "right" },
    { header: t("instellingen.gewichtsbandbreedte.col.max"), cell: (r) => fmtWeight(r.maxKg), align: "right" },
    { header: t("instellingen.gewichtsbandbreedte.col.source"), cell: (r) => r.sourceType ?? "—" },
    { header: t("instellingen.gewichtsbandbreedte.col.confidence"), cell: (r) => r.confidenceLevel ?? "—" },
    {
      header: t("instellingen.gewichtsbandbreedte.col.status"),
      cell: (r) => (
        <StatusBadge tone={statusTone(r.status)}>
          {t(`instellingen.gewichtsbandbreedte.status.${r.status}`)}
        </StatusBadge>
      ),
    },
    {
      header: t("instellingen.gewichtsbandbreedte.col.scope"),
      cell: (r) =>
        r.scope === "PLATFORM"
          ? t("instellingen.gewichtsbandbreedte.scope.platform")
          : t("instellingen.gewichtsbandbreedte.scope.vestiging"),
    },
  ];

  return (
    <div className="bwr-section">
      <div className="bwr-head">
        <div>
          <h2 className="bwr-title">{t("instellingen.gewichtsbandbreedte.title")}</h2>
          <p className="bwr-subtitle">
            {t("instellingen.gewichtsbandbreedte.subtitle", { args: [context.label] })}
          </p>
        </div>
        <Button variant="secondary" onClick={onBack}>
          {t("common.back")}
        </Button>
      </div>

      <div className="bwr-filter-row">
        <SelectField
          label={t("instellingen.gewichtsbandbreedte.speciesLabel")}
          className="bwr-filter-selector"
          value={selectedSpecies}
          onChange={(e) => setSelectedSpecies(e.target.value)}
          options={speciesOptions}
        />
        <SelectField
          label={t("instellingen.gewichtsbandbreedte.breedLabel")}
          className="bwr-filter-selector"
          value={selectedBreed}
          onChange={(e) => {
            setSelectedBreed(e.target.value);
            setSelectedRuleId(null);
          }}
          options={breedOptions}
        />
      </div>

      {selectedBreed === "" ? (
        <p className="bwr-empty">{t("instellingen.gewichtsbandbreedte.pickBreedHint")}</p>
      ) : (
        <>
          <div className="bwr-toolbar">
            <Button variant="primary" onClick={startCreate} disabled={isSaving}>
              <PlusCircleSolidIcon width={18} height={18} /> {t("instellingen.gewichtsbandbreedte.add")}
            </Button>
            <Button variant="secondary" onClick={startEdit} disabled={isSaving || !selectedRule}>
              {t("instellingen.gewichtsbandbreedte.edit")}
            </Button>
            <Button variant="secondary" onClick={deactivate} disabled={isSaving || !selectedRule}>
              {t("instellingen.gewichtsbandbreedte.remove")}
            </Button>
            <Button variant="secondary" onClick={review} disabled={isSaving || !selectedRule}>
              {t("instellingen.gewichtsbandbreedte.review")}
            </Button>
            <Button variant="secondary" onClick={aiSuggest} disabled={isSaving}>
              {t("instellingen.gewichtsbandbreedte.aiSuggest")}
            </Button>
          </div>

          {notice && <p className="bwr-notice">{notice}</p>}

          {rulesLoading ? (
            <p className="bwr-empty">{t("common.loading")}</p>
          ) : (
            <IndeelbareTabel<Rule> tabelKey="settings-breed-weight-ranges"
              columns={columns}
              rows={rules}
              getRowKey={(r) => String(r.id)}
              onRowClick={(r) => setSelectedRuleId(r.id)}
              selectedRowKey={selectedRuleId == null ? undefined : String(selectedRuleId)}
              empty={t("instellingen.gewichtsbandbreedte.empty")}
            />
          )}

          <p className="bwr-hint">{t("instellingen.gewichtsbandbreedte.conceptHint")}</p>
        </>
      )}

      <Modal
        open={edit != null}
        title={
          edit
            ? edit.id == null
              ? t("instellingen.gewichtsbandbreedte.create")
              : t("instellingen.gewichtsbandbreedte.editTitle")
            : ""
        }
        onClose={() => setEdit(null)}
        footer={
          edit && (
            <>
              <Button variant="secondary" onClick={() => setEdit(null)} disabled={isSaving}>
                {t("common.cancel")}
              </Button>
              <Button variant="primary" onClick={save} disabled={isSaving}>
                {t("common.save")}
              </Button>
            </>
          )
        }
      >
        {edit && (
          <div className="bwr-form">
            {formError && <p className="bwr-form-error">{formError}</p>}
            <div className="bwr-form-row">
              <Field
                label={t("instellingen.gewichtsbandbreedte.field.ageFrom")}
                type="number"
                inputMode="numeric"
                value={edit.ageFrom}
                onChange={(e) => setEdit({ ...edit, ageFrom: e.target.value })}
              />
              <Field
                label={t("instellingen.gewichtsbandbreedte.field.ageTo")}
                type="number"
                inputMode="numeric"
                value={edit.ageTo}
                onChange={(e) => setEdit({ ...edit, ageTo: e.target.value })}
              />
            </div>
            <div className="bwr-form-row">
              <SelectField
                label={t("instellingen.gewichtsbandbreedte.field.sex")}
                value={edit.sex}
                onChange={(e) => setEdit({ ...edit, sex: e.target.value })}
                options={[
                  { value: "", label: t("instellingen.gewichtsbandbreedte.field.both") },
                  { value: "M", label: "M" },
                  { value: "V", label: "V" },
                ]}
              />
              <SelectField
                label={t("instellingen.gewichtsbandbreedte.field.neutered")}
                value={edit.neutered}
                onChange={(e) => setEdit({ ...edit, neutered: e.target.value })}
                options={[
                  { value: "", label: t("instellingen.gewichtsbandbreedte.field.both") },
                  { value: "Wel", label: t("common.yes") },
                  { value: "Niet", label: t("common.no") },
                ]}
              />
            </div>
            <div className="bwr-form-row bwr-form-row-3">
              <Field
                label={t("instellingen.gewichtsbandbreedte.field.min")}
                value={edit.min}
                inputMode="decimal"
                onChange={(e) => setEdit({ ...edit, min: e.target.value })}
                required
              />
              <Field
                label={t("instellingen.gewichtsbandbreedte.field.ideal")}
                value={edit.ideal}
                inputMode="decimal"
                onChange={(e) => setEdit({ ...edit, ideal: e.target.value })}
              />
              <Field
                label={t("instellingen.gewichtsbandbreedte.field.max")}
                value={edit.max}
                inputMode="decimal"
                onChange={(e) => setEdit({ ...edit, max: e.target.value })}
                required
              />
            </div>
            <div className="bwr-form-row">
              <SelectField
                label={t("instellingen.gewichtsbandbreedte.field.confidence")}
                value={edit.confidence}
                onChange={(e) => setEdit({ ...edit, confidence: e.target.value })}
                options={[
                  { value: "LOW", label: "LOW" },
                  { value: "MEDIUM", label: "MEDIUM" },
                  { value: "HIGH", label: "HIGH" },
                ]}
              />
              <Field
                label={t("instellingen.gewichtsbandbreedte.field.sourceName")}
                value={edit.sourceName}
                onChange={(e) => setEdit({ ...edit, sourceName: e.target.value })}
              />
            </div>
            <Field
              label={t("instellingen.gewichtsbandbreedte.field.sourceUrl")}
              value={edit.sourceUrl}
              onChange={(e) => setEdit({ ...edit, sourceUrl: e.target.value })}
            />
            <Field
              label={t("instellingen.gewichtsbandbreedte.field.changeReason")}
              value={edit.changeReason}
              onChange={(e) => setEdit({ ...edit, changeReason: e.target.value })}
            />
            <div className="bwr-form-row">
              <CheckboxField
                label={t("instellingen.gewichtsbandbreedte.field.actief")}
                checked={edit.actief}
                onChange={(e) => setEdit({ ...edit, actief: e.target.checked })}
              />
              <CheckboxField
                label={t("instellingen.gewichtsbandbreedte.field.reviewed")}
                checked={edit.reviewed}
                onChange={(e) => setEdit({ ...edit, reviewed: e.target.checked })}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
